import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faShieldAlt,
  faEye,
  faGraduationCap,
  faBriefcase,
  faAward,
  faArrowLeft,
  faPlane,
  faCheckCircle,
  faTimesCircle,
  faClipboardCheck,
  faHandHoldingHeart,
} from "@fortawesome/free-solid-svg-icons";

import HeaderComponent from "../components/common/HeaderComponent";
import FooterComponent from "../components/common/FooterComponent";
import SuccessModalComponent from "../components/common/SuccessModalComponent";
import ErrorModalComponent from "../components/common/ErrorModalComponent";
import ActionConfirmModalComponent from "../components/common/ActionConfirmModalComponent";
import DangerActionConfirmModalComponent from "../components/common/DangerActionConfirmModalComponent";
import ImageModalComponent from "../components/common/ImageModalComponent";
import MilestonesTableComponent from "../components/common/MilestonesTableComponent";
import LoaderComponent from "../components/common/LoaderComponent";
import apiClient from "../api/apiClient";

export default function UserProfilePage() {
  const { referenceId } = useParams();

  const [membershipRequestData, setMembershipRequestData] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [trackingData, setTrackingData] = useState(null);

  const [loader, setLoader] = useState({
    show: false,
    message: "",
  });

  const [successModal, setSuccessModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  const [approveModal, setApproveModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  const [rejectionModal, setRejectionModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  const [errorModal, setErrorModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  const closeErrorModal = () => {
    setErrorModal({
      show: false,
      title: "",
      message: "",
    });
  };

  useEffect(() => {
    fetchMembershipRequestDetailsData();
    // eslint-disable-next-line
  }, [referenceId]);

  const fetchMembershipRequestDetailsData = async () => {
    try {
      setSuccessModal({
        show: false,
        title: "",
        message: "",
      });
      setLoader({
        show: true,
        message: "Loading membership request details. Please wait...",
      });
      const [registrationDetailsResponse, statusResponse] = await Promise.all([
        apiClient.post("/v1/memberships/get-request-details", {
          requestHeader: {
            requestId: `REQ-${Date.now()}`,
            requestTimestamp: new Date().toISOString(),
          },
          requestData: {
            referenceId: "#" + referenceId,
          },
        }),
        apiClient.post("/v1/memberships/check-request-status", {
          requestHeader: {
            requestId: `REQ-${Date.now()}-STATUS`,
            requestTimestamp: new Date().toISOString(),
          },
          requestData: {
            referenceId: "#" + referenceId,
          },
        }),
      ]);

      setMembershipRequestData(registrationDetailsResponse.data.responseData);
      generateTrackingDataFormat(statusResponse.data.responseData);
    } catch (err) {
      setErrorModal({
        show: true,
        title: "Failed to get membership request details",
        message:
          err.response?.data?.errorMessage ||
          "Unable to get membership request Please try again later.",
      });
    } finally {
      setLoader({
        show: false,
        message: "",
      });
    }
  };

  const generateTrackingDataFormat = (statusResponseData) => {
    let milestones = [...statusResponseData.registrationMilestones];
    const approved = milestones.find(
      (m) => m.status === "APPROVED" && m.completed
    );
    const rejected = milestones.find(
      (m) => m.status === "REJECTED" && m.completed
    );

    if (approved || rejected) {
      milestones = milestones.filter(
        (m) =>
          !(m.status === "APPROVED" && !m.completed) &&
          !(m.status === "REJECTED" && !m.completed)
      );
    } else {
      milestones = milestones.filter(
        (m) => m.status !== "APPROVED" && m.status !== "REJECTED"
      );
      milestones.push({
        status: "FINAL",
        displayName:
          "Membership Request Approved/Rejected by Executive Committee",
        completed: false,
        isPending: true,
        reason: null,
        completedAt: null,
      });
    }

    setTrackingData({
      ...statusResponseData,
      registrationMilestones: milestones,
    });
  };

  const handleOpenDocument = (presignedUrl) => {
    window.open(presignedUrl, "_blank");
  };

  const handleApprove = async () => {
    setApproveModal({
      show: true,
      title: "Confirm Membership Approval",
      message:
        "Are you sure you want to approve this membership request? Once approved, the applicant will be officially registered.",
    });
  };

  const confirmApprovalAndProceed = async () => {
    setApproveModal({
      show: false,
      title: "",
      message: "",
    });
    submitReview(
      "APPROVED",
      "Verification completed successfully, and the request is approved."
    );
  };

  const handleReject = async () => {
    setRejectionModal({
      show: true,
      title: "Confirm Membership Rejection",
      message:
        "Are you sure you want to reject this membership request? Once rejected, the applicant will not be registered.",
    });
  };

  const confirmRejectionAndProceed = async (note) => {
    setRejectionModal({
      show: false,
      title: "",
      message: "",
    });
    submitReview("REJECTED", note);
  };

  const submitReview = async (status, reason) => {
    try {
      setLoader({
        show: true,
        message: "Submitting review decesion. Please wait...",
      });

      const requestBody = {
        requestHeader: {
          requestId: `REQ-${Date.now()}`,
          requestTimestamp: new Date().toISOString(),
        },
        requestData: {
          referenceId: trackingData.referenceId,
          requestStatus: status,
          reason: reason,
        },
      };

      await apiClient.post("/v1/memberships/review-request", requestBody);

      setSuccessModal({
        show: true,
        title: "Success",
        message: `Membership Request has been ${status.toLowerCase()} successfully.`,
      });
    } catch (err) {
      setErrorModal({
        show: true,
        title: "Review Failed",
        message:
          err.response?.data?.errorMessage ||
          "Unable to submit review. Please try again.",
      });
    } finally {
      setLoader({
        show: false,
        message: "",
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      const d = new Date(dateString);
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const formatPhoneNumber = (number) => {
    return number || "N/A";
  };

  if (!membershipRequestData) {
    return (
      <div className="d-flex flex-column min-vh-100">
        <HeaderComponent user={null} />
        {/* <NavigationPanelComponent /> */}
        <main className="container my-5 flex-grow-1">
          <LoaderComponent
            show={loader.show}
            fullPage={true}
            message={loader.message}
          />
          <ErrorModalComponent
            show={errorModal.show}
            title={errorModal.title}
            message={errorModal.message}
            onClose={closeErrorModal}
          />
        </main>
        <FooterComponent />
      </div>
    );
  }

  const {
    personalDetails,
    contactDetails,
    schoolTimeline,
    studentEngagements,
    higherEducation,
    employmentDetails,
    donations,
    verificationDetails,
  } = membershipRequestData;

  return (
    <div className="d-flex flex-column min-vh-100 overflow-x-hidden">
      <HeaderComponent user={null} />
      {/* <NavigationPanelComponent /> */}

      <main className="container my-5 flex-grow-1">
        <LoaderComponent
          show={loader.show}
          fullPage={true}
          message={loader.message}
        />
        <ActionConfirmModalComponent
          show={approveModal.show}
          title={approveModal.title}
          message={approveModal.message}
          onConfirm={confirmApprovalAndProceed}
          onClose={() =>
            setApproveModal({
              show: false,
              title: "",
              message: "",
            })
          }
          confirmButtonText="Confirm"
          showCancelButton={true}
          cancelButtonText="Cancel"
        />
        <DangerActionConfirmModalComponent
          show={rejectionModal.show}
          title={rejectionModal.title}
          message={rejectionModal.message}
          onClose={() =>
            setRejectionModal({
              show: false,
              title: "",
              message: "",
            })
          }
          onConfirm={(note) => confirmRejectionAndProceed(note)}
          confirmButtonText="Confirm"
          cancelButtonText="Cancel"
          showNoteField={true}
          notePlaceholder="Please provide a reason for rejection..."
          noteLabel="Rejection Reason"
        />
        <SuccessModalComponent
          show={successModal.show}
          title={successModal.title}
          message={successModal.message}
          onClose={fetchMembershipRequestDetailsData}
        />
        <ErrorModalComponent
          show={errorModal.show}
          title={errorModal.title}
          message={errorModal.message}
          onClose={closeErrorModal}
        />
        {/* Back Button */}
        <div className="mb-4">
          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              window.history.back();
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back
          </button>
        </div>
        {/* Profile Header with Portrait */}
        <div className="row g-4 mb-4">
          <div className="col-lg-4">
            <div className="card rounded-3 border-vcg-muted h-100">
              <div className="card-body p-4 text-center">
                {/* Portrait */}
                <div className="mb-4">
                  {personalDetails?.portraitUrl ? (
                    <img
                      src={personalDetails.portraitUrl}
                      alt="Portrait"
                      className="rounded-3 border-vcg-muted"
                      style={{
                        width: 250,
                        height: 250,
                        objectFit: "fill",
                        borderColor: "#3C0008 !important",
                      }}
                      onClick={() => setShowImageModal(true)}
                    />
                  ) : (
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-circle bg-secondary"
                      style={{ width: 250, height: 250 }}
                    >
                      <FontAwesomeIcon
                        icon={faUser}
                        size="5x"
                        className="text-white"
                      />
                    </div>
                  )}
                  <ImageModalComponent
                    show={showImageModal}
                    imageUrl={personalDetails.portraitUrl}
                    altText="Portrait"
                    onClose={() => setShowImageModal(false)}
                  />
                </div>
                <h4 className="fw-bold mb-2">{personalDetails.fullName}</h4>
                <p className="text-muted mb-3">
                  ({personalDetails.callingName})
                </p>

                <div className="d-flex align-items-center justify-content-center mb-2">
                  <small className="text-muted me-2">NIC Number : </small>
                  <span className="fw-bold">{personalDetails.nicNumber}</span>
                </div>
                <div className="d-flex align-items-center justify-content-center">
                  <small className="text-muted me-2">Date of Birth : </small>
                  <span className="fw-bold">
                    {formatDate(personalDetails.dateOfBirth)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Membership Request Review Section */}
          <div className="col-lg-8">
            <div className="card rounded-3 border-vcg-muted h-100">
              <div className="card-body p-4 d-flex flex-column">
                <h5 className="fw-bold mb-4">
                  <FontAwesomeIcon icon={faClipboardCheck} className="me-2" />
                  Membership Request Status
                </h5>

                <div className="row g-3 flex-grow-1">
                  <div className="col-md-6">
                    <div
                      className="p-3 rounded-3"
                      style={{ background: "#f8f9fa" }}
                    >
                      <small className="text-muted d-block mb-1">
                        Reference ID
                      </small>
                      <h6 className="mb-0 fw-bold text-break">
                        {trackingData.referenceId}
                      </h6>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div
                      className="p-3 rounded-3"
                      style={{ background: "#f8f9fa" }}
                    >
                      <small className="text-muted d-block mb-1">
                        Membership Number
                      </small>
                      <h6 className="mb-0 fw-bold">
                        {trackingData.membershipNumber || "N/A"}
                      </h6>
                    </div>
                  </div>

                  <div className="col-12">
                    <div
                      className="p-3 rounded-3"
                      style={{ background: "#f8f9fa" }}
                    >
                      <small className="text-muted d-block mb-1">
                        Current Status
                      </small>
                      <h6 className="mb-0 fw-bold">
                        {trackingData.currentStatusDisplayName}
                      </h6>
                    </div>
                  </div>

                  {trackingData.currentStatus === "REJECTED" && (
                    <div className="col-12">
                      <div
                        className="p-3 rounded-3 border border-danger"
                        style={{ background: "#fff5f5" }}
                      >
                        <small className="text-danger d-block mb-1">
                          Rejection Reason
                        </small>
                        <h6 className="mb-0 fw-bold text-danger">
                          {trackingData.registrationMilestones.find(
                            (m) => m.status === "REJECTED"
                          )?.reason || "No reason provided"}
                        </h6>
                      </div>
                    </div>
                  )}

                  <div className="col-12">
                    <div
                      className="p-3 rounded-3"
                      style={{ background: "#f8f9fa" }}
                    >
                      <small className="text-muted d-block mb-1">
                        Last Updated
                      </small>
                      <h6 className="mb-0 fw-bold">
                        {formatDateTime(trackingData.lastUpdatedDateTime)}
                      </h6>
                    </div>
                  </div>
                </div>
                {trackingData.currentStatus === "APPROVED" ||
                trackingData.currentStatus === "REJECTED" ? (
                  <div className="mt-3">
                    <div
                      className={`alert ${
                        trackingData.currentStatus === "APPROVED"
                          ? "alert-success"
                          : "alert-danger"
                      } mb-0 d-flex align-items-center`}
                    >
                      <FontAwesomeIcon
                        icon={
                          trackingData.currentStatus === "APPROVED"
                            ? faCheckCircle
                            : faTimesCircle
                        }
                        className="me-3"
                        size="2x"
                      />
                      <div>
                        <strong>
                          Membership Request has received its final decision and
                          is{" "}
                          {trackingData.currentStatus === "APPROVED"
                            ? "Approved"
                            : "Rejected"}
                        </strong>
                        <p className="mb-0 mt-1">
                          {trackingData.currentStatus === "APPROVED"
                            ? "This Membership Request has been approved by the Executive Committee."
                            : "This Membership Request has been rejected by the Executive Committee."}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex gap-3 justify-content-end mt-3">
                    <button
                      className="btn btn-success btn-lg"
                      onClick={handleApprove}
                      disabled={loader.show}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      Approve Request
                    </button>

                    <button
                      className="btn btn-danger btn-lg"
                      onClick={handleReject}
                      disabled={loader.show}
                    >
                      <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                      Reject Request
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Contact Information Section */}
        <div className="card rounded-3 border-vcg-muted mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-4">
              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
              Contact Information
            </h5>

            <div className="row g-3">
              <div className="col-md-6">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">Email</small>
                  <h6 className="mb-0 fw-bold text-break">
                    {contactDetails.email}
                  </h6>
                </div>
              </div>

              <div className="col-md-6">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">
                    Mobile Number
                  </small>
                  <h6 className="mb-0 fw-bold">
                    {formatPhoneNumber(contactDetails.mobileNumber)}
                  </h6>
                </div>
              </div>

              <div className="col-md-6">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">
                    WhatsApp Number
                  </small>
                  <h6 className="mb-0 fw-bold">
                    {formatPhoneNumber(contactDetails.whatsappNumber)}
                  </h6>
                </div>
              </div>

              <div className="col-md-6">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">Fixed Line</small>
                  <h6 className="mb-0 fw-bold">
                    {formatPhoneNumber(contactDetails.fixedLineNumber)}
                  </h6>
                </div>
              </div>

              <div className="col-12">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">Address</small>
                  <h6 className="mb-0 fw-bold">{contactDetails.address}</h6>
                  <small className="text-muted">
                    {contactDetails.city}, {contactDetails.district},{" "}
                    {contactDetails.province}
                  </small>
                </div>
              </div>
              <div className="col-12">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">Country</small>
                  {contactDetails.abroad && (
                    <small className="text-primary d-block mb-1">
                      <FontAwesomeIcon icon={faPlane} className="me-1" />
                      Currently Abroad
                    </small>
                  )}
                  <h6 className="mb-0 fw-bold">{contactDetails.country}</h6>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* School Timeline */}
        <div className="card rounded-3 border-vcg-muted mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-4">
              <FontAwesomeIcon icon={faGraduationCap} className="me-2" />
              School Timeline
            </h5>

            <div className="row g-3">
              <div className="col-md-4">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">
                    Enrollment Number (Admission Number)
                  </small>
                  <h6 className="mb-0 fw-bold">
                    {schoolTimeline.enrollmentNumber || "N/A"}
                  </h6>
                </div>
              </div>

              <div className="col-md-4">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">
                    Enrolled Grade
                  </small>
                  <h6 className="mb-0 fw-bold">
                    Grade {schoolTimeline.enrollmentGrade} (
                    {schoolTimeline.enrollmentYear})
                  </h6>
                </div>
              </div>

              <div className="col-md-4">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">
                    Last Passed Grade
                  </small>
                  <h6 className="mb-0 fw-bold">
                    Grade {schoolTimeline.lastPassedGrade} (
                    {schoolTimeline.lastPassedYear})
                  </h6>
                </div>
              </div>

              <div className="col-md-6">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">Batch</small>
                  <h6 className="mb-0 fw-bold">{schoolTimeline.batch}</h6>
                </div>
              </div>

              {schoolTimeline.advancedLevelStreamName && (
                <div className="col-md-6">
                  <div
                    className="p-3 rounded-3"
                    style={{ background: "#f8f9fa" }}
                  >
                    <small className="text-muted d-block mb-1">
                      Advanced Level Stream
                    </small>
                    <h6 className="mb-0 fw-bold">
                      {schoolTimeline.advancedLevelStreamName || "N/A"}
                    </h6>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Student Engagements */}
        <div className="card rounded-3 border-vcg-muted mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-4">
              <FontAwesomeIcon icon={faAward} className="me-2" />
              Student Engagements
            </h5>

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>Designation</th>
                    <th>Start Year</th>
                    <th>End Year</th>
                  </tr>
                </thead>
                <tbody>
                  {studentEngagements.map((engagement, idx) => (
                    <tr key={idx}>
                      <td>{engagement.designation}</td>
                      <td>{engagement.startYear}</td>
                      <td>{engagement.endYear}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Higher Education */}
        <div className="card rounded-3 border-vcg-muted mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-4">
              <FontAwesomeIcon icon={faGraduationCap} className="me-2" />
              Higher Education
            </h5>

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>Level</th>
                    <th>Qualification</th>
                    <th>Institute</th>
                    <th>Year</th>
                  </tr>
                </thead>
                <tbody>
                  {higherEducation.map((edu, idx) => (
                    <tr key={idx}>
                      <td>{edu.level}</td>
                      <td>{edu.qualification}</td>
                      <td>{edu.institute}</td>
                      <td>{edu.year}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Employment Details */}
        <div className="card rounded-3 border-vcg-muted mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-4">
              <FontAwesomeIcon icon={faBriefcase} className="me-2" />
              Employment Details
            </h5>

            <div className="row g-3">
              <div className="col-md-4">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">Industry</small>
                  <h6 className="mb-0 fw-bold">
                    {employmentDetails.industry || "N/A"}
                  </h6>
                </div>
              </div>

              <div className="col-md-4">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">Profession</small>
                  <h6 className="mb-0 fw-bold">
                    {employmentDetails.profession || "N/A"}
                  </h6>
                </div>
              </div>

              <div className="col-md-4">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">Designation</small>
                  <h6 className="mb-0 fw-bold">
                    {employmentDetails.designation || "N/A"}
                  </h6>
                </div>
              </div>

              <div className="col-md-6">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">
                    Employer Name
                  </small>
                  <h6 className="mb-0 fw-bold">
                    {employmentDetails.employerName || "N/A"}
                  </h6>
                </div>
              </div>

              <div className="col-md-6">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">
                    Office Number
                  </small>
                  <h6 className="mb-0 fw-bold">
                    {formatPhoneNumber(employmentDetails.officeNumber)}
                  </h6>
                </div>
              </div>

              <div className="col-12">
                <div
                  className="p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <small className="text-muted d-block mb-1">
                    Employer Address
                  </small>
                  <h6 className="mb-0 fw-bold">
                    {employmentDetails.employerAddress || "N/A"}
                  </h6>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Donation Details */}
        <div className="card rounded-3 border-vcg-muted mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-4">
              <FontAwesomeIcon icon={faHandHoldingHeart} className="me-2" />
              Donation History
            </h5>

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Reason</th>
                    <th>Document</th>
                  </tr>
                </thead>
                <tbody>
                  {donations
                    .filter((d) => d.donationAmount)
                    .map((donation, idx) => (
                      <tr key={idx}>
                        <td>{formatDate(donation.donationDate)}</td>
                        <td className="fw-bold">
                          LKR {donation.donationAmount?.toLocaleString()}
                        </td>
                        <td>{donation.donationReason || "-"}</td>
                        <td>
                          {donation.donationSlipUrl ? (
                            <button
                              className="btn btn-dark btn-sm"
                              onClick={() =>
                                handleOpenDocument(donation.donationSlipUrl)
                              }
                            >
                              <FontAwesomeIcon icon={faEye} className="me-2" />
                              View
                            </button>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Documents Section */}
        <div className="card rounded-3 border-vcg-muted mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-4">
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
              Verification Documents
            </h5>

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>Document Type</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Portrait */}
                  {personalDetails?.portraitUrl && (
                    <tr>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="fw-semibold">Photograph</span>
                        </div>
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-dark btn-sm"
                          onClick={() =>
                            handleOpenDocument(personalDetails.portraitUrl)
                          }
                        >
                          <FontAwesomeIcon icon={faEye} className="me-2" />
                          View
                        </button>
                      </td>
                    </tr>
                  )}
                  {/* Proof Document */}
                  {verificationDetails?.proofDocumentUrl && (
                    <tr>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="fw-semibold">Proof Document</span>
                        </div>
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-dark btn-sm"
                          onClick={() =>
                            handleOpenDocument(
                              verificationDetails.proofDocumentUrl
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faEye} className="me-2" />
                          View
                        </button>
                      </td>
                    </tr>
                  )}
                  {/* Payment Slip */}
                  {verificationDetails?.paymentSlipUrl && (
                    <tr>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="fw-semibold">Payment Slip</span>
                        </div>
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-dark btn-sm"
                          onClick={() =>
                            handleOpenDocument(
                              verificationDetails.paymentSlipUrl
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faEye} className="me-2" />
                          View
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* MILESTONES TABLE */}
        <MilestonesTableComponent
          title="Membership Request Milestones"
          milestones={trackingData.registrationMilestones}
          showReviewedBy={true}
        />
      </main>

      <FooterComponent />
    </div>
  );
}
